import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, IconButton, LinearProgress, Typography } from "@mui/material";
import {
  ArrowRight,
  BookOpen,
  Bot,
  CalendarClock,
  Check,
  CheckCircle2,
  Circle,
  FolderKanban,
  Rocket,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import apiClient from "../api-client";
import { brand } from "../theme/theme";
import { Button } from "./Button";
import { UnifiedModal } from "./UnifiedModal";
import { useAuth } from "./contexts/AuthContext";
import { ONBOARDING_KEY, WELCOME_PROGRESS_EVENT, isWelcomeEligible } from "../Tutorials";
import { WelcomeSidebarTour } from "../Tutorials/WelcomeSidebarTour";
import { useSidebar } from "./Sidebar";
import { ClientModal } from "../Pages/Clients/ClientModal";
import { ClientCreatedModal } from "../Pages/Clients/seoSetup/ClientCreatedModal";
import { SETUP_ITEMS } from "../Pages/Clients/seoSetup/seoSetupUtils";
import { SeoConnectModal } from "../Pages/Clients/seoSetup/SeoConnectModal";
import { GoogleConnectOfferModal } from "../Pages/Clients/seoSetup/GoogleConnectOfferModal";


const FIRST_STEPS = [
  {
    id: "first_client",
    icon: UserPlus,
    title: "Añade tu primer cliente",
    description: "Crea su espacio de trabajo y decide si necesita el Setup recomendado.",
    actionLabel: "Crear cliente",
    to: "/clients?welcome=create-client",
  },
  {
    id: "client_portal",
    icon: Users,
    title: "Dale acceso a su portal",
    description: "Entra en los detalles del cliente que acabas de crear y genera allí su enlace de invitación.",
    actionLabel: "Abrir clientes",
    to: "/clients",
  },
  {
    id: "first_project",
    icon: FolderKanban,
    title: "Organiza el primer trabajo",
    description: "Crea un proyecto y convierte el siguiente paso en algo visible para todos.",
    actionLabel: "Ir a proyectos",
    to: "/projects/custom",
  },
];

const nowIso = () => new Date().toISOString();

export function CommercialTour({ updateUiPreferences, onClientCreated }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isMobile, openMobile } = useSidebar();
  const storedState = user?.ui_preferences?.[ONBOARDING_KEY] || {};
  const [localState, setLocalState] = useState(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCheckingProgress, setIsCheckingProgress] = useState(false);
  const [detectedSteps, setDetectedSteps] = useState([]);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const setupConnectedRef = useRef(false);
  const promptedUserRef = useRef(null);
  const completionPromptedRef = useRef(false);
  const tourResumedRef = useRef(false);

  const onboardingState = localState || storedState;
  const completedSteps = useMemo(
    () => new Set(detectedSteps),
    [detectedSteps]
  );
  const completedCount = completedSteps.size;
  const allStepsCompleted = completedCount === FIRST_STEPS.length;
  const progress = (completedCount / FIRST_STEPS.length) * 100;
  const tourCompleted = Boolean(onboardingState.sidebar_tour_completed_at);
  // Antes del recorrido hay que crear el primer cliente y decidir su setup SEO/GEO.
  // Vive en ui_preferences, así que vuelve a salir tras cerrar sesión hasta completarlo.
  // Quien ya tenía cliente antes de existir este paso no tiene `welcome_client` y se lo salta.
  const gateClient = onboardingState.welcome_client;
  const gateCompleted = Boolean(
    onboardingState.client_gate_completed_at ||
      onboardingState.sidebar_tour_started_at ||
      (completedSteps.has("first_client") && !gateClient)
  );
  const eligible = isWelcomeEligible(user) && !onboardingState.completed_at;
  // Con el cliente creado en /bienvenida el recorrido va primero y el setup SEO/GEO se ofrece al terminarlo
  const tourFirst = gateCompleted || Boolean(gateClient);
  const seoOfferOpen = eligible && tourCompleted && Boolean(gateClient) && !onboardingState.client_gate_completed_at;

  useEffect(() => {
    if (!eligible || onboardingState.intro_seen_at || promptedUserRef.current === user?.id) return;
    promptedUserRef.current = user.id;
    setIntroOpen(true);
  }, [eligible, onboardingState.intro_seen_at, user?.id]);

  // El recorrido no se puede saltar: si se recargó a medias, se retoma
  useEffect(() => {
    if (
      !eligible ||
      !onboardingState.sidebar_tour_started_at ||
      tourCompleted ||
      tourResumedRef.current
    ) return;
    tourResumedRef.current = true;
    setPanelOpen(false);
    setTourOpen(true);
  }, [eligible, onboardingState.sidebar_tour_started_at, tourCompleted]);

  useEffect(() => {
    if (!eligible) return undefined;
    let active = true;

    const loadProgress = async () => {
      setIsCheckingProgress(true);
      try {
        const response = await apiClient.get("users/welcome-progress/");
        if (!active) return;
        setDetectedSteps(
          FIRST_STEPS.filter((step) => response.data?.[step.id]).map((step) => step.id)
        );
      } catch (error) {
        console.error("No se pudo comprobar el progreso de bienvenida:", error);
      } finally {
        if (active) {
          setIsCheckingProgress(false);
          setProgressLoaded(true);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void loadProgress();
    };

    void loadProgress();
    window.addEventListener(WELCOME_PROGRESS_EVENT, loadProgress);
    window.addEventListener("focus", loadProgress);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      active = false;
      window.removeEventListener(WELCOME_PROGRESS_EVENT, loadProgress);
      window.removeEventListener("focus", loadProgress);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [eligible, user?.id]);

  let gateStage = null;
  if (eligible && !gateCompleted && progressLoaded && onboardingState.intro_seen_at) {
    if (!gateClient) gateStage = "client";
    else gateStage = onboardingState.seo_setup_skipped_at ? "google" : "seo";
  }

  useEffect(() => {
    if (
      !eligible ||
      !gateCompleted ||
      !allStepsCompleted ||
      !onboardingState.intro_seen_at ||
      tourOpen ||
      seoOfferOpen ||
      setupOpen ||
      completionPromptedRef.current
    ) return;

    completionPromptedRef.current = true;
    setPanelOpen(false);
    setCompletionOpen(true);
  }, [allStepsCompleted, eligible, gateCompleted, onboardingState.intro_seen_at, tourOpen, seoOfferOpen, setupOpen]);

  if (!eligible) return null;

  const persist = async (patch) => {
    const nextState = { ...onboardingState, ...patch };
    setLocalState(nextState);
    setIsSaving(true);
    try {
      await updateUiPreferences({ [ONBOARDING_KEY]: nextState });
    } catch (error) {
      console.error("No se pudo guardar el progreso de bienvenida:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const openPanel = () => {
    if (tourCompleted) {
      setPanelOpen(true);
      return;
    }
    tourResumedRef.current = true;
    setTourOpen(true);
    void persist({
      intro_seen_at: onboardingState.intro_seen_at || nowIso(),
      sidebar_tour_started_at: onboardingState.sidebar_tour_started_at || nowIso(),
    });
  };

  const openSetup = () => {
    setupConnectedRef.current = false;
    setSetupOpen(true);
  };

  const handleStart = () => {
    setIntroOpen(false);
    if (tourFirst) {
      openPanel();
      return;
    }
    void persist({ intro_seen_at: onboardingState.intro_seen_at || nowIso() });
  };

  const handleCreateClient = async (payload) => {
    const response = await apiClient.post("clients/", payload);
    const client = response.data;
    if (payload.consultoresAsignados?.length) {
      try {
        await apiClient.post(`clients/${client.id}/assign-consultants/`, {
          consultant_ids: payload.consultoresAsignados,
        });
      } catch (error) {
        console.error("Error asignando consultores:", error);
      }
    }
    setDetectedSteps((previous) => [...previous, "first_client"]);
    onClientCreated?.();
    void persist({ welcome_client: { id: client.id, name: client.name } });
    return client;
  };

  const finishGate = () => {
    setIntroOpen(false);
    tourResumedRef.current = true;
    setTourOpen(true);
    void persist({
      intro_seen_at: onboardingState.intro_seen_at || nowIso(),
      client_gate_completed_at: nowIso(),
      sidebar_tour_started_at: nowIso(),
    });
  };

  const handleSeoSkip = () => {
    void persist({ seo_setup_skipped_at: nowIso() });
  };

  const finishSeoOffer = () => {
    setSetupOpen(false);
    setPanelOpen(true);
    void persist({ client_gate_completed_at: nowIso() });
  };

  // Antes del recorrido, cerrar el setup sin lanzarlo cuenta como rechazado y se le ofrece Google si no se hizo ya
  const handleSetupClose = () => {
    setSetupOpen(false);
    if (tourCompleted) finishSeoOffer();
    else if (setupConnectedRef.current || onboardingState.google_offered_at) finishGate();
    else handleSeoSkip();
  };

  const handleTourFinish = () => {
    setTourOpen(false);
    setPanelOpen(!gateClient || Boolean(onboardingState.client_gate_completed_at));
    void persist({ sidebar_tour_completed_at: nowIso() });
  };


  const handleFinish = () => {
    setPanelOpen(false);
    setCompletionOpen(true);
  };

  const handleCompletionClose = () => {
    setCompletionOpen(false);
    void persist({ completed_steps: Array.from(completedSteps), completed_at: nowIso() });
  };

  const handleStepAction = (step) => {
    setIntroOpen(false);
    setPanelOpen(false);
    if (!onboardingState.intro_seen_at) {
      void persist({ intro_seen_at: nowIso() });
    }
    navigate(step.to);
  };

  return (
    <>
      <UnifiedModal
        open={introOpen}
        onClose={() => {}}
        hideCloseButton
        title="Bienvenido a Markezy"
        maxWidth="md"
        contentSx={{ p: 0 }}
      >
        <Box data-tutorial-active="true" data-tutorial-surface="welcome">
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              px: { xs: 2.5, sm: 5 },
              pt: { xs: 3, sm: 4.5 },
              pb: { xs: 2.75, sm: 4 },
              background: `linear-gradient(135deg, ${brand.soft} 0%, #FFFFFF 58%, #FFF7ED 100%)`,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 210,
                height: 210,
                borderRadius: "50%",
                right: -72,
                top: -98,
                background: brand.gradientDiagonal,
                opacity: 0.13,
              }}
            />
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.6,
                mb: 2,
                borderRadius: 999,
                color: brand.softText,
                bgcolor: "rgba(255,255,255,.82)",
                border: `1px solid ${brand.softBorder}`,
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: ".04em",
                textTransform: "uppercase",
              }}
            >
              <Rocket size={14} /> Tu espacio ya está listo
            </Box>
            <Typography
              component="h2"
              sx={{
                maxWidth: 620,
                mb: 1.25,
                color: "text.primary",
                fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                fontWeight: 800,
                lineHeight: 1.08,
                letterSpacing: "-.035em",
              }}
            >
              Empieza con trabajo real desde el primer minuto.
            </Typography>
            <Typography sx={{ maxWidth: 610, color: "text.secondary", lineHeight: 1.65 }}>
              {tourFirst
                ? "Estos tres pasos dejan tu agencia preparada. Antes, te enseñamos en un minuto dónde está cada cosa para que te muevas por Markezy con soltura."
                : "Estos tres pasos dejan tu agencia preparada. Empezamos dando de alta tu primer cliente y después te enseñamos en un minuto dónde está cada cosa."}
            </Typography>
          </Box>

          <Box sx={{ px: { xs: 2.5, sm: 5 }, py: { xs: 2.5, sm: 3.5 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(3, minmax(0, 1fr))" },
                gap: { xs: 1.25, sm: 2 },
              }}
            >
              {FIRST_STEPS.map((step, index) => {
                const Icon = step.icon;
                return (
                  <Box
                    key={step.id}
                    sx={{
                      p: { xs: 1.75, sm: 2.25 },
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 3,
                      bgcolor: "background.paper",
                      display: { xs: "grid", sm: "block" },
                      gridTemplateColumns: "40px minmax(0, 1fr)",
                      columnGap: 1.5,
                      alignItems: "start",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: { xs: 0, sm: 2 } }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 2.25,
                          color: brand.softText,
                          bgcolor: brand.soft,
                        }}
                      >
                        <Icon size={20} />
                      </Box>
                      <Typography sx={{ display: { xs: "none", sm: "block" }, color: "text.disabled", fontSize: 12, fontWeight: 800 }}>
                        0{index + 1}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ mb: 0.5, fontWeight: 750 }}>{step.title}</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
                        {step.description}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "stretch", sm: "flex-end" }, mt: 3 }}>
              <Button onClick={handleStart} disabled={isSaving}>
                {tourFirst ? "Empezar recorrido" : "Crear mi primer cliente"} <ArrowRight size={16} />
              </Button>
            </Box>
          </Box>
        </Box>
      </UnifiedModal>

      <UnifiedModal
        open={seoOfferOpen && !setupOpen}
        onClose={() => {}}
        hideCloseButton
        title="Recorrido terminado"
        maxWidth="sm"
        contentSx={{ p: 0 }}
      >
        {gateClient && (
          <Box data-tutorial-active="true" data-tutorial-surface="welcome-seo">
            <Box sx={{ px: { xs: 2.5, sm: 4 }, pt: { xs: 3, sm: 4 }, pb: 3, background: `linear-gradient(135deg, ${brand.soft} 0%, #FFFFFF 58%, #FFF7ED 100%)` }}>
              <Box sx={{ width: 48, height: 48, display: "grid", placeItems: "center", mb: 2, borderRadius: "50%", color: "common.white", background: brand.gradientDiagonal, boxShadow: brand.shadow }}>
                <Check size={24} />
              </Box>
              <Typography component="h2" sx={{ mb: 1, fontSize: "clamp(1.5rem, 2.6vw, 2rem)", fontWeight: 800, lineHeight: 1.1, letterSpacing: "-.03em" }}>
                Ya has terminado el recorrido
              </Typography>
              <Typography sx={{ color: "text.secondary", lineHeight: 1.65 }}>
                ¿Quieres vincular ahora los datos SEO y GEO de tu primer cliente,{" "}
                <Box component="strong" sx={{ color: "text.primary" }}>{gateClient.name}</Box>?
              </Typography>
            </Box>
            <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 3 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.25 }}>
                {SETUP_ITEMS.map(({ icon: Icon, text }) => (
                  <Box key={text} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
                    <Box sx={{ width: 36, height: 36, display: "grid", placeItems: "center", borderRadius: 2, color: brand.softText, bgcolor: brand.soft }}>
                      <Icon size={18} />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 650 }}>{text}</Typography>
                  </Box>
                ))}
              </Box>
              <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                También puedes hacerlo más tarde desde la ficha del cliente, en «Activos Digitales».
              </Typography>
              <Box sx={{ display: "flex", flexDirection: { xs: "column-reverse", sm: "row" }, justifyContent: "flex-end", gap: 1, mt: 3 }}>
                <Button variant="outline" onClick={finishSeoOffer} disabled={isSaving}>
                  Más tarde
                </Button>
                <Button onClick={openSetup} disabled={isSaving}>
                  Vincular datos SEO y GEO <ArrowRight size={16} />
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </UnifiedModal>

      <UnifiedModal
        open={completionOpen}
        onClose={handleCompletionClose}
        disableBackdropClose
        title="Ya tienes lo esencial"
        maxWidth="md"
        contentSx={{ p: 0 }}
      >
        <Box data-tutorial-active="true" data-tutorial-surface="welcome-complete">
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              px: { xs: 2.5, sm: 5 },
              pt: { xs: 3, sm: 4.5 },
              pb: { xs: 2.75, sm: 4 },
              background: `linear-gradient(135deg, ${brand.soft} 0%, #FFFFFF 58%, #FFF7ED 100%)`,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                display: "grid",
                placeItems: "center",
                mb: 2,
                borderRadius: "50%",
                color: "common.white",
                background: brand.gradientDiagonal,
                boxShadow: brand.shadow,
              }}
            >
              <Check size={24} />
            </Box>
            <Typography
              component="h2"
              sx={{
                maxWidth: 620,
                mb: 1.25,
                fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                fontWeight: 800,
                lineHeight: 1.08,
                letterSpacing: "-.035em",
              }}
            >
              Tu agencia ya está preparada para trabajar.
            </Typography>
            <Typography sx={{ maxWidth: 650, color: "text.secondary", lineHeight: 1.65 }}>
              A partir de aquí puedes aprovechar más herramientas de Markezy para ahorrar tiempo y
              centralizar el trabajo de tu equipo.
            </Typography>
          </Box>

          <Box sx={{ px: { xs: 2.5, sm: 5 }, py: { xs: 2.5, sm: 3.5 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(3, minmax(0, 1fr))" },
                gap: { xs: 1.25, sm: 2 },
              }}
            >
              {[
                {
                  icon: Bot,
                  title: "Consulta al copiloto",
                  description: "Pídele información sobre clientes, proyectos y actividad sin tener que buscarla página por página.",
                },
                {
                  icon: CalendarClock,
                  title: "Genera y programa contenidos",
                  description: "Crea piezas, adáptalas a cada canal y organiza su publicación desde un mismo flujo.",
                },
                {
                  icon: BookOpen,
                  title: "Descubre los tutoriales",
                  description: "Dentro de las páginas encontrarás tutoriales específicos para aprender cada herramienta cuando la necesites.",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Box
                    key={item.title}
                    sx={{
                      p: { xs: 1.75, sm: 2.25 },
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 3,
                      display: { xs: "grid", sm: "block" },
                      gridTemplateColumns: "40px minmax(0, 1fr)",
                      columnGap: 1.5,
                      alignItems: "start",
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        display: "grid",
                        placeItems: "center",
                        mb: { xs: 0, sm: 2 },
                        borderRadius: 2.25,
                        color: brand.softText,
                        bgcolor: brand.soft,
                      }}
                    >
                      <Icon size={20} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ mb: 0.5, fontWeight: 750 }}>{item.title}</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                gap: 2,
                mt: 3,
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Los tutoriales están disponibles desde ordenador dentro de cada sección.
              </Typography>
              <Button onClick={handleCompletionClose}>
                Empezar a explorar <ArrowRight size={16} />
              </Button>
            </Box>
          </Box>
        </Box>
      </UnifiedModal>

      {gateStage === "client" && (
        <ClientModal isOpen required onClose={() => {}} onSave={handleCreateClient} />
      )}

      {gateStage === "seo" && !setupOpen && (
        <ClientCreatedModal
          open
          client={gateClient}
          onSkip={handleSeoSkip}
          onStart={openSetup}
        />
      )}

      {/* Sin `client`: el stepper carga la ficha completa (web, idioma…) para sus valores por defecto */}
      {setupOpen && (
        <SeoConnectModal
          open
          onOpenChange={(open) => !open && handleSetupClose()}
          clientId={gateClient.id}
          currentLink={null}
          onConnected={() => {
            setupConnectedRef.current = true;
          }}
        />
      )}

      {gateStage === "google" && (
        <GoogleConnectOfferModal open client={gateClient} onFinish={finishGate} />
      )}

      {tourOpen && <WelcomeSidebarTour user={user} onFinish={handleTourFinish} />}

      {/* En móvil se oculta con el menú abierto para no tapar sus opciones */}
      {!introOpen && !gateStage && !setupOpen && !seoOfferOpen && !panelOpen && !completionOpen && !tourOpen && !(isMobile && openMobile) && (
        <Button
          onClick={openPanel}
          aria-label={`Primeros pasos, ${completedCount} de ${FIRST_STEPS.length} completados`}
          sx={{
            position: "fixed",
            right: { xs: 12, md: 24 },
            bottom: { xs: "calc(12px + env(safe-area-inset-bottom))", md: 24 },
            zIndex: 1250,
            height: { xs: 34, md: 44 },
            px: { xs: 1.5, md: 2.25 },
            gap: { xs: "6px", md: "8px" },
            fontSize: { xs: "0.8125rem", md: "0.875rem" },
            boxShadow: "0 12px 30px rgba(15, 23, 42, .18)",
          }}
        >
          <Rocket size={isMobile ? 14 : 17} />
          {isMobile ? "Primeros pasos" : "Primeros pasos ·"} {completedCount}/{FIRST_STEPS.length}
        </Button>
      )}

      {panelOpen && (
        <Box
          data-tutorial-active="true"
          data-tutorial-surface="welcome-checklist"
          sx={{
            position: "fixed",
            left: { xs: 12, sm: "auto" },
            right: { xs: 12, sm: 24 },
            bottom: { xs: "calc(12px + env(safe-area-inset-bottom))", sm: 24 },
            zIndex: 1250,
            width: { xs: "auto", sm: 390 },
            maxHeight: { xs: "calc(100dvh - 88px)", sm: "calc(100dvh - 112px)" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3.5,
            boxShadow: "0 24px 64px rgba(15, 23, 42, .2)",
          }}
        >
          <Box sx={{ flexShrink: 0, p: 2.5, pb: 2, background: `linear-gradient(135deg, ${brand.soft} 0%, #fff 85%)` }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 17 }}>Pon tu agencia en marcha</Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "text.secondary" }}>
                  {completedCount} de {FIRST_STEPS.length} pasos completados
                </Typography>
              </Box>
              <IconButton size="small" aria-label="Minimizar primeros pasos" onClick={() => setPanelOpen(false)}>
                <X size={18} />
              </IconButton>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ mt: 1.75, height: 7, borderRadius: 999, bgcolor: "rgba(255,255,255,.9)", "& .MuiLinearProgress-bar": { background: brand.gradient } }}
            />
          </Box>

          <Box sx={{ p: 1.25, minHeight: 0, overflowY: "auto" }}>
            {FIRST_STEPS.map((step) => {
              const done = completedSteps.has(step.id);
              return (
                <Box
                  key={step.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "32px minmax(0, 1fr)", sm: "32px minmax(0, 1fr) auto" },
                    gap: { xs: "8px 10px", sm: 1.25 },
                    alignItems: "center",
                    p: 1.25,
                    borderRadius: 2.5,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box
                    aria-label={done ? `${step.title} completado` : `${step.title} pendiente`}
                    sx={{ display: "grid", placeItems: "center", color: done ? "success.main" : "text.disabled" }}
                  >
                    {done ? <CheckCircle2 size={22} /> : <Circle size={22} />}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 750, textDecoration: done ? "line-through" : "none", color: done ? "text.secondary" : "text.primary" }}
                    >
                      {step.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.35, display: "block" }}>
                      {step.description}
                    </Typography>
                  </Box>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => handleStepAction(step)}
                    sx={{ gridColumn: { xs: "2", sm: "auto" }, justifySelf: "start" }}
                  >
                    {step.actionLabel} <ArrowRight size={14} />
                  </Button>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.75, borderTop: "1px solid", borderColor: "divider" }}>
            <Button variant="ghost" size="xs" onClick={() => setPanelOpen(false)}>Ocultar guía</Button>
            {allStepsCompleted ? (
              <Button size="xs" onClick={handleFinish} disabled={isSaving}>
                <Check size={14} /> Terminar
              </Button>
            ) : isSaving || isCheckingProgress ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary" }}>
                <CircularProgress size={13} />
                <Typography variant="caption">Actualizando</Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                <Check size={13} />
                <Typography variant="caption">Progreso automático</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}

CommercialTour.propTypes = {
  updateUiPreferences: PropTypes.func.isRequired,
  onClientCreated: PropTypes.func,
};
